/**
 * PlansPage (/plans) — vitrine de planos + "Minha Assinatura".
 *
 * ESCOPO 1 (sem pagamento): a grade é uma VITRINE; o CTA dos planos pagos fica
 * "Em breve" (disabled). O plano ATUAL e o USO vêm de /v1/me/entitlements (dados reais).
 * Quando o Billing (Escopo 2) chegar, só trocamos o CTA por checkout (e, se quisermos,
 * passamos a grade a vir de um endpoint de catálogo).
 *
 * Obs.: a grade abaixo é a proposta comercial (cotas serão ajustadas pelo André) —
 * é copy de marketing, não a fonte de verdade dos limites (essa é o backend).
 */
import { useState } from 'react';
import {
  Box, CardContent, Typography, Button, Chip, Stack,
  ToggleButton, ToggleButtonGroup, LinearProgress, Divider, List, ListItem, ListItemIcon, ListItemText,
  Tooltip, CircularProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import StandardCard from '../components/ui/StandardCard';
import AppSnackbar from '../components/ui/AppSnackbar';
import { useEntitlements, useLimit } from '../hooks/useEntitlements';
import { useSnackbar } from '../hooks/useSnackbar';
import billingService from '../services/billingService';

const PLANS = [
  { slug: 'free', name: 'Grátis', monthly: 0, yearly: 0,
    highlights: ['1 local', '2 profissionais', '5 serviços', '50 clientes', '100 agendamentos futuros'] },
  { slug: 'essencial', name: 'Essencial', monthly: 2990, yearly: 29900,
    highlights: ['3 locais', '10 profissionais', '20 serviços', '500 clientes', 'Logo personalizado'] },
  { slug: 'pro', name: 'Pro', monthly: 8990, yearly: 89900, featured: true,
    highlights: ['10 locais', '30 profissionais', 'Serviços ilimitados', '5.000 clientes', 'Relatórios avançados', 'WhatsApp'] },
  { slug: 'business', name: 'Business', monthly: 19990, yearly: 199900,
    highlights: ['Tudo ilimitado', 'Domínio próprio nas Páginas', 'Suporte prioritário'] },
];

const USAGE_ROWS = [
  ['places', 'Locais'],
  ['professionals', 'Profissionais'],
  ['services', 'Serviços'],
  ['persons', 'Clientes'],
  ['schedules', 'Agendas'],
  ['appointments_future', 'Agendamentos futuros'],
];

const PLAN_RANK = { free: 0, essencial: 1, pro: 2, business: 3 };

// Cor ouro para o card em destaque
const GOLD = '#B8860B';
const GOLD_BG = 'rgba(184, 134, 11, 0.08)';
const GOLD_BORDER = 'rgba(184, 134, 11, 0.55)';

function formatBRLNoDecimals(cents) {
  return `R$ ${Math.round(cents / 100).toLocaleString('pt-BR')}`;
}

function UsageRow({ limitKey, label }) {
  const { limit, current, unlimited, nearLimit } = useLimit(limitKey);
  const pct = unlimited || !limit ? 0 : Math.min(100, Math.round((current / limit) * 100));
  return (
    <Box sx={{ mb: 1 }}>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Typography variant="body2" color={nearLimit ? 'warning.main' : 'text.disabled'}>
          {current} / {unlimited ? '∞' : limit}
        </Typography>
      </Stack>
      {!unlimited && (
        <LinearProgress
          variant="determinate"
          value={pct}
          color={nearLimit ? 'warning' : 'primary'}
          sx={{ height: 4, borderRadius: 2, opacity: 0.7 }}
        />
      )}
    </Box>
  );
}

// Determina qual card deve ser destacado como "próximo passo" para upgrade
function getNextPlanSlug(currentSlug) {
  const sorted = [...PLANS].sort((a, b) => PLAN_RANK[a.slug] - PLAN_RANK[b.slug]);
  const currentRank = PLAN_RANK[currentSlug] ?? 0;
  const next = sorted.find(p => PLAN_RANK[p.slug] > currentRank);
  return next?.slug ?? null;
}

export default function PlansPage() {
  const [cycle, setCycle] = useState('yearly');
  const [loadingSlug, setLoadingSlug] = useState(null);
  const { planSlug, planName, status, scheduledPlanSlug } = useEntitlements();
  const { snackbar, showError, hideSnackbar } = useSnackbar();

  const handleSubscribe = async (slug) => {
    try {
      setLoadingSlug(slug);
      const { checkoutUrl } = await billingService.createOrder({
        kind: 'subscription_new', planSlug: slug, billingCycle: cycle, methods: ['CreditCard'],
      });
      window.location.href = checkoutUrl;   // redireciona ao gateway
    } catch (e) {
      showError(e?.response?.data?.message || 'Não foi possível iniciar o checkout. Tente novamente.');
      setLoadingSlug(null);
    }
  };

  const currentRank = PLAN_RANK[planSlug] ?? 0;
  // O plano "mais popular" original (featured) só é destacado em ouro quando
  // o usuário ainda não o possui. Se já possui, o destaque passa para o próximo.
  const nextPlanSlug = getNextPlanSlug(planSlug);
  const featuredSlug = currentRank >= (PLAN_RANK['pro'] ?? 2) ? nextPlanSlug : 'pro';

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom>Planos</Typography>

      {/* Toggle mensal/anual — no topo, mais proeminente */}
      <Stack direction="row" justifyContent="center" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={cycle}
          onChange={(_, v) => v && setCycle(v)}
        >
          <ToggleButton value="monthly">Mensal</ToggleButton>
          <ToggleButton value="yearly" sx={{ fontWeight: 700 }}>Anual (2 meses grátis)</ToggleButton>
        </ToggleButtonGroup>
        {cycle === 'yearly' && (
          <Tooltip title="O valor anual é cobrado em parcelas mensais no cartão — uma única contratação.">
            <Chip
              size="small"
              label="12x sem juros · cobrança única"
              variant="outlined"
              color="success"
              sx={{ cursor: 'default', fontWeight: 500 }}
            />
          </Tooltip>
        )}
      </Stack>

      {/* Grade de planos */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,1fr)', md: 'repeat(4,1fr)' }, gap: 2.5, mb: 3 }}>
        {PLANS.map((plan) => {
          const isCurrent = plan.slug === planSlug;
          const isFeatured = plan.slug === featuredSlug;
          const monthlyEquiv = Math.round(plan.yearly / 12);
          const isFree = plan.slug === 'free';
          const isDowngrade = PLAN_RANK[plan.slug] < currentRank;

          return (
            <StandardCard
              key={plan.slug}
              noAccent
              sx={{
                position: 'relative',
                borderColor: isCurrent
                  ? 'primary.main'
                  : isFeatured
                    ? GOLD_BORDER
                    : 'divider',
                borderWidth: (isCurrent || isFeatured) ? 2 : 1,
                background: isFeatured ? GOLD_BG : undefined,
                transition: 'box-shadow 0.2s ease',
                '&:hover': { boxShadow: 4 },
              }}
            >
              {/* Badge topo-direita: plano atual OU mais popular/próximo */}
              {isCurrent ? (
                <Chip
                  size="small"
                  color="primary"
                  label="Plano atual"
                  sx={{ position: 'absolute', top: 8, right: 8, fontWeight: 600 }}
                />
              ) : isFeatured && !isFree ? (
                <Chip
                  icon={currentRank >= (PLAN_RANK['pro'] ?? 2) ? <TrendingUpIcon /> : <StarIcon />}
                  size="small"
                  label={currentRank >= (PLAN_RANK['pro'] ?? 2) ? 'Próximo passo' : 'Mais popular'}
                  sx={{
                    position: 'absolute', top: 8, right: 8,
                    background: GOLD, color: '#fff', fontWeight: 700,
                    '& .MuiChip-icon': { color: '#fff' },
                  }}
                />
              ) : null}

              <CardContent sx={{ pt: isFeatured || isCurrent ? 5 : 3, pb: 3, px: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>{plan.name}</Typography>

                <Box sx={{ my: 1.5 }}>
                  {isFree ? (
                    <Typography variant="h4" component="span" sx={{ fontWeight: 800 }}>R$ 0</Typography>
                  ) : cycle === 'yearly' ? (
                    <>
                      <Typography variant="h4" component="span" sx={{ fontWeight: 800 }}>
                        {formatBRLNoDecimals(monthlyEquiv)}
                      </Typography>
                      <Typography variant="body2" component="span" color="text.secondary"> /mês</Typography>
                      <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.25 }}>
                        {formatBRLNoDecimals(plan.yearly)}/ano · cobrado à vista parcelado em 12x
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography variant="h4" component="span" sx={{ fontWeight: 800 }}>
                        {formatBRLNoDecimals(plan.monthly)}
                      </Typography>
                      <Typography variant="body2" component="span" color="text.secondary"> /mês</Typography>
                    </>
                  )}
                </Box>

                <Divider sx={{ my: 1.5 }} />

                <List dense disablePadding>
                  {plan.highlights.map((h) => (
                    <ListItem key={h} disableGutters sx={{ py: 0.4 }}>
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        <CheckCircleIcon fontSize="small" color="success" />
                      </ListItemIcon>
                      <ListItemText primaryTypographyProps={{ variant: 'body2' }} primary={h} />
                    </ListItem>
                  ))}
                </List>

                <Box sx={{ mt: 2.5 }}>
                  {isCurrent ? (
                    <Button fullWidth variant="outlined" disabled>Plano atual</Button>
                  ) : isFree ? (
                    <Button fullWidth variant="text" disabled>{isDowngrade ? 'Plano gratuito' : 'Grátis'}</Button>
                  ) : isDowngrade ? (
                    <Tooltip title="O downgrade é agendado para o fim do ciclo (em breve em Minha Assinatura).">
                      <span><Button fullWidth variant="outlined" disabled>Fazer downgrade</Button></span>
                    </Tooltip>
                  ) : (
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={() => handleSubscribe(plan.slug)}
                      disabled={loadingSlug != null}
                      startIcon={loadingSlug === plan.slug ? <CircularProgress size={16} color="inherit" /> : null}
                      sx={isFeatured ? { background: `${GOLD} !important`, color: '#fff !important' } : undefined}
                    >
                      {loadingSlug === plan.slug ? 'Redirecionando…' : 'Assinar'}
                    </Button>
                  )}
                </Box>
              </CardContent>
            </StandardCard>
          );
        })}
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 3, textAlign: 'center' }}>
        Pagamento seguro via gateway. Assinaturas no cartão; o ciclo anual é cobrado parcelado em 12x.
      </Typography>

      {/* Minha Assinatura — abaixo e com menos ênfase */}
      <StandardCard noAccent fillHeight={false} sx={{ opacity: 0.85 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
            <Typography variant="subtitle1" color="text.secondary">Uso atual</Typography>
            <Chip size="small" variant="outlined" color="default" label={planName} sx={{ fontSize: '0.7rem' }} />
            {status !== 'active' && <Chip size="small" color="warning" label={status} />}
            {scheduledPlanSlug && (
              <Chip size="small" variant="outlined" label={`Troca agendada → ${scheduledPlanSlug}`} />
            )}
          </Stack>
          {USAGE_ROWS.map(([key, label]) => (
            <UsageRow key={key} limitKey={key} label={label} />
          ))}
        </CardContent>
      </StandardCard>

      <AppSnackbar snackbar={snackbar} onClose={hideSnackbar} />
    </Box>
  );
}
